import { MediaType } from "../media/MediaType";
import { Format, MediaProtocol } from "../media/MediaTypes";
import type { IFileOrStreamOutput } from "../media/MediaOutputs";

interface ILinkData {
    url: string;
    queryString: string | null;
}

interface ISharedLinkCfgJson {
    accountHttpTemplate: IHttpLinkTemplateJson;
    mediaFileRSHttpTemplate: IHttpLinkTemplateJson;
    parentAccountHttpTemplate: IHttpLinkTemplateJson;
    playerHttpTemplate: IHttpLinkTemplateJson;
}

interface ISharedLinkCfg {
    accountHttpTemplate: HttpLinkTemplate;
    mediaFileRSHttpTemplate: HttpLinkTemplate;
    parentAccountHttpTemplate: HttpLinkTemplate;
    playerHttpTemplate: HttpLinkTemplate;
}

interface IHttpLinkTemplateJson {
    httpUrl: ILinkData;
    httpDLUrl: ILinkData | null;
    expires: number;
}

interface  IMediaLinkTemplatesJson{
    mediaLinkTemplates: IMediaLinkTemplateJson[];
}

interface IMediaPlayerMediaLinkTemplateResponse{
    mediaLinkTemplate: IMediaLinkTemplateJson;
}

interface IMediaLinkTemplateJson {
    mediaId: number;
    httpUrl: ILinkData | null;
    httpDLUrl: ILinkData | null;
    hlsAdaptiveUrl: ILinkData | null;
    hlsUrl: ILinkData | null;
    ssaiUrl: ILinkData | null;
    expires: number;
}

interface IHttpLinkTemplateData {
    httpUrl: Link;
    httpDLUrl: Link | null;
    expires: number;
}

interface IMediaLinkTemplateData {
    mediaId: number;
    httpUrl: Link | null;
    httpDLUrl: Link | null;
    hlsAdaptiveUrl: Link | null;
    hlsUrl: Link | null;
    ssaiUrl: Link | null;
    expires: number;
}

class Link {
    data: ILinkData;

    constructor(data: ILinkData) {
        this.data = data;
    }

    clone(): Link {
        return new Link({
            url: this.data.url,
            queryString: this.data.queryString
        });
    }

    urlWithQueryString() {
        const link = this.data;
        // in case URL already had ? present.
        const prefixChar = link.url.indexOf("?") > -1 ? '&' : '?';
        const urlWithQS = link.queryString ? `${link.url}${prefixChar}${link.queryString}` : link.url;
        return urlWithQS;
    }

}

class HttpLinkTemplate {
    data: IHttpLinkTemplateData;

    constructor(data: IHttpLinkTemplateJson) {
        this.data = {
            httpUrl: new Link(data.httpUrl),
            httpDLUrl: data.httpDLUrl ? new Link(data.httpDLUrl) : null,
            expires: data.expires
        };
    }

}

class MediaLinkTemplate {
    data: IMediaLinkTemplateData;

    constructor(data: IMediaLinkTemplateJson) {
        this.data = {
            mediaId: data.mediaId,
            hlsAdaptiveUrl: data.hlsAdaptiveUrl ? new Link(data.hlsAdaptiveUrl) : null,
            hlsUrl: data.hlsUrl ? new Link(data.hlsUrl) : null,
            httpUrl: data.httpUrl ? new Link(data.httpUrl) : null,
            httpDLUrl: data.httpDLUrl ? new Link(data.httpDLUrl) : null,
            ssaiUrl: data.ssaiUrl ? new Link(data.ssaiUrl) : null,
            expires: data.expires
        }
    }
}

enum LinkTemplateToken {
    FileId = "{{fileId}}",
    Format = "{{format}}",
    MediaId = "{{mediaId}}",
    MediaStreamOutputNumber = "{{msoNum}}",
    PlayerId = "{{playerId}}",
    Prepend = "{{prepend}}" //wowza prepend
}

class LinkStoreBase {
    protected accountLinkTemplate: HttpLinkTemplate | null;
    protected idToMediaLinkTemplate: Map<number, MediaLinkTemplate>;
    protected mediaFileRSLinkTemplate: HttpLinkTemplate | null;
    protected parentAccountLinkTemplate: HttpLinkTemplate | null;
    protected playerLinkTemplate: HttpLinkTemplate | null;

    constructor() {
        this.accountLinkTemplate = null;
        this.mediaFileRSLinkTemplate = null;
        this.idToMediaLinkTemplate = new Map<number, MediaLinkTemplate>();
        this.parentAccountLinkTemplate = null;
        this.playerLinkTemplate = null;
    }

    protected nowTS(): number {
        return Date.now() / 1000;
    }

    private buildLinkTemplate(tokenToValue: Map<string, string>, templateLink: Link): Link {
        let newUrl = templateLink.data.url;
        Array.from(tokenToValue.entries()).map(pair => {
            const key = pair[0];
            const value = pair[1];
            newUrl = newUrl.replace(key, value);
        });
        return new Link({
            url: newUrl,
            queryString: templateLink.data.queryString
        });
    }

    // getMediaPosterOrThumb(fileId: number): Link {
    //     return this.getMediaFileRSHttpLink(fileId);
    // }

    getLinkFromFileOrStream(mediaProtocol: MediaProtocol, mediaOutput: IFileOrStreamOutput): Link | null {
        switch (mediaOutput.mediaType) {
            case MediaType.Video:
                return this.getLinkVideo(mediaOutput, mediaProtocol);

            case MediaType.VideoStream:
                return this.getLinkVideostream(mediaOutput, mediaProtocol);

            default:
                throw new Error(`mediaType: ${mediaOutput.mediaType} not implemented`);
        }
    }

    protected getLinkVideo(mediaOutput: IFileOrStreamOutput, mediaProtocol: MediaProtocol): Link | null {
        switch (mediaProtocol) {
            case MediaProtocol.Hls:
                return this.getVideoHlsLink(mediaOutput);

            default:
                throw new Error(`not implemented`);
        }
    }

    // // get link based on fileId or mediaStreamOutputId, based on user selection in player
    // protected getLinkVideo(mediaId: number, fileId: number, format: string, mediaProtocol: MediaProtocol): Link | null {
    //     switch (mediaProtocol) {
    //         case MediaProtocol.Hls:
    //             return this.getMediaFileHlsLink(mediaId, fileId, format);
    //         case MediaProtocol.Http:
    //             return this.getMediaFileHttpLink(mediaId, fileId, format,false);
    //         default:
    //             throw new Error(`not implemented`);
    //     }
    // }

    // get link based on fileId or mediaStreamOutputId, based on user selection in player
    getLinkMediaDL(mediaId: number, fileId: number, format: string): Link | null {
        return this.getMediaFileHttpLink(mediaId, fileId, format, true);
    }

    // get link based on fileId or mediaStreamOutputId, based on user selection in player
    protected getLinkVideostream(mediaOutput: IFileOrStreamOutput, mediaProtocol: MediaProtocol): Link | null {
        switch (mediaProtocol) {
            case MediaProtocol.Hls:
                return this.getVideostreamHlsLink(mediaOutput);
            default:
                throw new Error(`not implemented`);
        }
    }

    private _getAccountHttpLink(linkTemplate: HttpLinkTemplate | null, fileId: number, format: string, isDownload: boolean): Link {
        if (linkTemplate) {
            const httpLinkTemplate = isDownload ? linkTemplate.data.httpDLUrl : linkTemplate.data.httpUrl;
            if(httpLinkTemplate) {
                const tokenToValue = new Map([
                    [LinkTemplateToken.FileId, fileId.toString()],
                    [LinkTemplateToken.Format, format]]);
                return this.buildLinkTemplate(tokenToValue, httpLinkTemplate);
            } else {
                throw new Error(`Cannot get account link template, dl template may be unavailable. fileId: ${fileId}, format: ${format}, isDownload: ${isDownload}, is dl template null: ${linkTemplate.data.httpDLUrl == null}`);
            }
        } else {
            throw new Error(`Cannot get account link because template is missing.`);
        }
    }

    getAccountHttpLink(fileId: number, format: string, isDownload: boolean): Link {
        const linkTemplate = this.accountLinkTemplate;
        return this._getAccountHttpLink(linkTemplate, fileId, format, isDownload);
    }

    getParentAccountHttpLink(fileId: number, format: string, isDownload: boolean): Link {
        const linkTemplate = this.parentAccountLinkTemplate;
        return this._getAccountHttpLink(linkTemplate, fileId, format, isDownload);
    }

    getMediaFileRSHttpLink(mediaId: number, fileId: number, format: string, isDownload: boolean): Link {
        const linkTemplate = this.mediaFileRSLinkTemplate;
        if (linkTemplate) {
            const httpLinkTemplate = isDownload ? linkTemplate.data.httpDLUrl : linkTemplate.data.httpUrl;
            if(httpLinkTemplate) {
                const tokenToValue = new Map([
                    [LinkTemplateToken.MediaId, mediaId.toString()],
                    [LinkTemplateToken.FileId, fileId.toString()],
                    [LinkTemplateToken.Format, format]]);
                return this.buildLinkTemplate(tokenToValue, httpLinkTemplate);
            } else {
                throw new Error(`Cannot get mediaFileRS link template, dl template may be unavailable. fileId: ${fileId}, format: ${format}, isDownload: ${isDownload}, is dl template null: ${linkTemplate.data.httpDLUrl == null}`);
            }
        } else {
            throw new Error(`Cannot get mediaFileRS link because template is missing.`);
        }
    }

    getMediaFileHttpLink(mediaId: number, fileId: number, format: string, isDownload: boolean): Link | null {
        const linkTemplate = this.idToMediaLinkTemplate.get(mediaId);
        if (linkTemplate) {
            const urlTemplate = isDownload ? linkTemplate.data.httpDLUrl : linkTemplate.data.httpUrl;
            if (urlTemplate) {
                const tokenToValue = new Map([
                    [LinkTemplateToken.FileId, fileId.toString()],
                    [LinkTemplateToken.Format, format]
                ]);
                return this.buildLinkTemplate(tokenToValue, urlTemplate);
            } else {
                return null;
            }
        } else {
            return null;
        }
    }

    getPlayerFileHttpLink(playerId: number, fileId: number, format: string, isDownload: boolean): Link {
        const linkTemplate = this.playerLinkTemplate;
        if (linkTemplate) {
            const httpLinkTemplate = isDownload ? linkTemplate.data.httpDLUrl : linkTemplate.data.httpUrl;
            if(httpLinkTemplate) {
                const tokenToValue = new Map([
                    [LinkTemplateToken.PlayerId, playerId.toString()],
                    [LinkTemplateToken.FileId, fileId.toString()],
                    [LinkTemplateToken.Format, format]]);
                return this.buildLinkTemplate(tokenToValue, httpLinkTemplate);
            } else {
                throw new Error(`Cannot get player link template, dl template may be unavailable. playerId: ${playerId}, fileId: ${fileId}, format: ${format}, isDownload: ${isDownload}, is dl template null: ${linkTemplate.data.httpDLUrl == null}`);
            }
        } else {
            throw new Error(`Cannot get player link because template is missing.`);
        }
    }

    private getVideoHlsLink(mediaOutput: IFileOrStreamOutput): Link | null {
        const linkTemplate = this.idToMediaLinkTemplate.get(mediaOutput.mediaId);
        if (linkTemplate) {
            if (linkTemplate.data.hlsUrl) {
                if(mediaOutput.isAdaptive && linkTemplate.data.hlsAdaptiveUrl){
                    const tokenToValue: Map<string, string> = new Map([]);
                    return this.buildLinkTemplate(tokenToValue, linkTemplate.data.hlsAdaptiveUrl);
                } else if(!mediaOutput.isAdaptive && linkTemplate.data.hlsUrl) {
                    const tokenToValue = new Map([
                        [LinkTemplateToken.Prepend, this.getWowzaPrepend(mediaOutput.format)],
                        [LinkTemplateToken.FileId, mediaOutput.id.toString()],
                        [LinkTemplateToken.Format, mediaOutput.format]
                    ]);
                    return this.buildLinkTemplate(tokenToValue, linkTemplate.data.hlsUrl);
                } else {
                    return null;
                }
            } else {
                return null;
            }
        } else {
            return null;
        }
    }

    private getVideostreamHlsLink(mediaOutput: IFileOrStreamOutput): Link | null {
        const linkTemplate = this.idToMediaLinkTemplate.get(mediaOutput.mediaId);
        if (linkTemplate) {
            if(mediaOutput.isAdaptive && linkTemplate.data.hlsAdaptiveUrl){
                const tokenToValue: Map<string, string> = new Map([]);
                return this.buildLinkTemplate(tokenToValue, linkTemplate.data.hlsAdaptiveUrl);
            } else if(!mediaOutput.isAdaptive && linkTemplate.data.hlsUrl) {
                const tokenToValue = new Map([
                    [LinkTemplateToken.MediaStreamOutputNumber, mediaOutput.outputNumber.toString()]
                ]);
                return this.buildLinkTemplate(tokenToValue, linkTemplate.data.hlsUrl);
            } else {
                return null;
            }
        } else {
            return null;
        }
    }

    private getWowzaPrepend(format: String): string {
        const prependMP4 = "mp4:";
        const prependMP3 ="mp3:";
        const prependSMIL = "smil:";
        switch(format){
            case Format.MP4:
                return prependMP4;
            case Format.M4A:
                return prependMP4;
            case Format.MP3:
                return prependMP3;
            case Format.SMIL:
                return prependSMIL;
            default:
                return prependMP4;
        }
    }

    setAccountLinkTemplate(newTemplate: HttpLinkTemplate): void {
        this.accountLinkTemplate = newTemplate;
    }

    setMediaFileRSLinkTemplate(newTemplate: HttpLinkTemplate): void {
        this.mediaFileRSLinkTemplate = newTemplate;
    }

    setMediaLinkTemplate(newTemplate: MediaLinkTemplate): void {
        this.idToMediaLinkTemplate.set(newTemplate.data.mediaId, newTemplate);
    }

    setPlayerLinkTemplate(newTemplate: HttpLinkTemplate): void {
        this.playerLinkTemplate = newTemplate;
    }



}

export type {
    ILinkData,
    IMediaLinkTemplateJson,
    IMediaLinkTemplatesJson,
    IMediaPlayerMediaLinkTemplateResponse,
    IHttpLinkTemplateJson,
    ISharedLinkCfg,
    ISharedLinkCfgJson,
};

export {
    HttpLinkTemplate,
    MediaLinkTemplate,
    Link,
    LinkStoreBase,
};

